var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { IdGenerator } from '@inter-ikea-kompis/utilities';
import { Collapsible } from '@inter-ikea-kompis/component-collapsible';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';
import { chevronDownSmall, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import AccordionItemAriaDisabledEnum from '../../enums/AccordionItemAriaDisabledEnum.js';
import AccordionItemStyle from './AccordionItemStyle.js';
/**
 * Component that can render in an opened or closed state.
 *
 * @event expand Emits when clicking on the accordion item
 */
export default class AccordionItem extends KompisElement {
    constructor() {
        super(...arguments);
        // The title of the accordion item
        this.label = null;
        // The caption of the accordion item
        this.caption = null;
        // Removes horizontal padding
        this.noHorizontalPadding = false;
        // Expands the accordion item and reveals the content within the slot
        this.expanded = false;
        // Should be set to true when an expanded accordion item is not allowed to be minimized again until another item is expanded, in which case the previous item, now minimized, should be set to false and the current expanded item should now be set to true
        this.ariaDisabled = AccordionItemAriaDisabledEnum.false;
        // Disables you from interacting with the accordion item
        this.disabled = false;
        this.accordionUniqueId = new IdGenerator().generateId();
        this.collapsibleUniqueId = new IdGenerator().generateId();
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        return html `
			<style>
				${AccordionItemStyle.getStyle({
            expanded: this.expanded,
            noHorizontalPadding: this.noHorizontalPadding,
            theme: this.theme,
            disabled: this.disabled
        })}
			</style>
			<button
				id="${this.accordionUniqueId}"
				aria-controls="${this.collapsibleUniqueId}"
				aria-expanded="${this.expanded}"
				aria-disabled="${this.ariaDisabled}"
				?disabled="${this.disabled}"
				@click="${() => this.dispatchEvent(new CustomEvent('expand', { bubbles: true, detail: { expanded: !this.expanded } }))}"
			>
				<div class="textContainer">
					<kompis-text
						class="header"
						.theme="${this.theme}"
						.type="${ThemeFontStyleTypeEnum.bodyMedium}"
						.bold="${true}"
						>${this.label}</kompis-text
					>
					${this.getCaption()}
				</div>
				<div class="spacing"></div>
				<skapa-icon class="icon" icon="chevron-down-small"></skapa-icon>
				<div class="outline"></div>
			</button>
			<kompis-collapsible .expanded="${this.expanded}">
				<div
					role="region"
					id="${this.collapsibleUniqueId}"
					aria-labelledby="${this.accordionUniqueId}"
					aria-hidden="${!this.expanded}"
				>
					<slot></slot>
				</div>
			</kompis-collapsible>
		`;
    }
    /**
     * When focusing the accordion item the button should receive the focus.
     */
    focus() {
        this.shadowRoot?.querySelector('button')?.focus();
    }
    async connectedCallback() {
        registerIcon(chevronDownSmall);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-icon', Icon);
        this.customElements.define('kompis-collapsible', Collapsible);
        super.connectedCallback();
    }
    getCaption() {
        if (!this.caption) {
            return null;
        }
        return html `
			<kompis-text
				class="caption"
				.theme="${this.theme}"
				.type="${ThemeFontStyleTypeEnum.bodyMedium}"
				>${this.caption}</kompis-text
			>
		`;
    }
}
__decorate([
    property({ type: String })
], AccordionItem.prototype, "label", void 0);
__decorate([
    property({ type: String })
], AccordionItem.prototype, "caption", void 0);
__decorate([
    property({ type: Object, required: true })
], AccordionItem.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean })
], AccordionItem.prototype, "noHorizontalPadding", void 0);
__decorate([
    property({ type: Boolean })
], AccordionItem.prototype, "expanded", void 0);
__decorate([
    property({ type: String })
], AccordionItem.prototype, "ariaDisabled", void 0);
__decorate([
    property({ type: Boolean })
], AccordionItem.prototype, "disabled", void 0);
//# sourceMappingURL=AccordionItem.js.map